import {
    IDatasetTypeMetadata,
    IDynamicDatasetTypeMetadata
} from 'app/extensibility/metadatas/resource-type/dataset-type-metadata';
import { getOracleCopySinkControls, getOracleCopySourceControls, oracleV2NumberSettingsMetadata } from '../consts/metadata-consts';
import type { ControlMetadata } from 'app/extensibility/metadatas/control/control-metadata';
import { adfModelTypeMap } from '../consts/adf-model-type-map-const';
import { getServiceInstance } from 'app/multi-contexts/modules/root/root.application';
import { ExposureControlService } from 'app/services/exposure-control/base-exposure-control.service';
import { format } from 'app/shared/utils/string.util';
import { GlobalVariables } from 'app/services/globals';
import { binaryPhysicalTypes, ColumnPhysicalType, ColumnType } from 'app/shared/utils/column-type.util';
import { UpdateMethod } from 'shell-models-lib';
import { getAppendOption, getSCD2Option, getUpsertOption } from '../consts/copy-job-const';

export const oracleDatasetMetadata: IDynamicDatasetTypeMetadata = async (injector) => {
    const exposureControlService = await getServiceInstance<ExposureControlService>(injector, 'ExposureControlService');
    const metadata: IDatasetTypeMetadata = {
        type: adfModelTypeMap.oracle.datasetType,
        supportLinkedServiceTypes: [adfModelTypeMap.oracle.linkedServiceType],
        displayName: stringResources.Oracle_DisplayText,
        showWarningMessage: '{ linkedService && linkedService.version !== "v2" }',
        warningMessage: format(
            stringResources.Dataset_Legacy_IsNotSupportedWarning_alt3,
            stringResources.VersionLabel.toLocaleLowerCase()
        ),
        warningMessageHyperlink: 'https://learn.microsoft.com/azure/data-factory/connector-oracle',
        datasetKind: 'table',
        queryTooltipLink: 'https://aka.ms/oraclequerytips',
        isSource: true,
        isSink: true,
        icon: {
            className: 'svg-i-100x100-Oracle'
        },
        isFirstClass: true,
        supportCreatedAsV2: true,
        splitTablePath: true,
        notSupportPhysicalType: !GlobalVariables.oracleAutoCreate,
        v1SupportSchema: true,
        supportDataFlowSource: () => exposureControlService.enableOracleDataFlow,
        category: ['Database'],
        supportParallelRead: true,
        supportAutoTableCreation: GlobalVariables.oracleAutoCreate,
        supportEditPhysicalType: GlobalVariables.oracleAutoCreate,
        copySourceControls: getOracleCopySourceControls(adfModelTypeMap.oracle.copySourceType),
        copySinkControls: getOracleCopySinkControls(),
        dataflowSourceControls: [
            {
                type: 'radio-switch',
                bind: 'useQuery',
                label: stringResources.InputLabel,
                options: [
                    {
                        label: stringResources.ModelName_Table,
                        value: false
                    },
                    {
                        label: stringResources.QueryLabel,
                        value: true
                    }
                ],
                cases: [
                    {
                        caseValue: true,
                        children: [
                            {
                                type: 'parameterizable',
                                child: {
                                    type: 'textarea',
                                    bind: 'query',
                                    label: stringResources.QueryLabel,
                                    isDataFlowExpression: true
                                },
                                dynamicContentTextClass: 'input-spaced',
                                showErrorIcon: false,
                                isDataFlowExpression: true
                            }
                        ]
                    }
                ]
            },
            {
                type: 'parameterizable',
                child: {
                    type: 'textbox-number',
                    bind: 'timeout',
                    label: stringResources.Timeout,
                    description: stringResources.Oracle_Timeout_Description,
                    isDataFlowExpression: true
                },
                dynamicContentTextClass: 'input-spaced',
                showErrorIcon: false,
                isDataFlowExpression: true
            }
        ],
        copyJob: {
            sourceControls: GlobalVariables.copyJobOracleNumberSettings ? [{
                ...oracleV2NumberSettingsMetadata,
                isActivityLevel: true,
                // Copy Job uses Fabric connections and owns defaults on each table/query model.
                hide: false,
                children: (oracleV2NumberSettingsMetadata.children as ControlMetadata[]).map((child) => ({
                    ...child,
                    child: {
                        ...child.child,
                        isLabelVertical: true
                    }
                }))
            }] : [],
            fullLoadBehavior: {
                supportFullLoadBehavior: true
            },
            multipleQuery: true,
            updateMethod: GlobalVariables.oracleUpsert
                ? {
                      supportUpdateMethod: true,
                      options: GlobalVariables.copyJobOracleCDCSink
                          ? [getAppendOption(), getUpsertOption(), getSCD2Option()]
                          : [getAppendOption(), getUpsertOption()],
                      updateMethodProperties: ['writeBehavior', 'upsertSettings'],
                      defaultUpdateMethod: UpdateMethod.Append,
                      upsertKeyColumnRequired: true
                  }
                : undefined,
            sourcePartition: {
                supportSourcePartition: true,
                onlyInteger: true
            }
        },
        supportSameTypePhysicalCopy: GlobalVariables.oracleAutoCreate,
        mappingKeyToLowerCase: GlobalVariables.oracleAutoCreate,
        supportedPhysicalTypes: GlobalVariables.oracleAutoCreate ? [
            ColumnPhysicalType.Boolean,
            ColumnPhysicalType.Date,
            ColumnPhysicalType.Timestamp,
            ColumnPhysicalType.BinaryDouble,
            ColumnPhysicalType.BinaryFloat,
            ColumnPhysicalType.TimestampTimeZone,
            ColumnPhysicalType.TimestampLocalTimeZone,
            ColumnPhysicalType.IntervalDaySecond,
            ColumnPhysicalType.IntervalYearMonth,
            ColumnPhysicalType.Number,
            ColumnPhysicalType.Nchar,
            ColumnPhysicalType.Char,
            ColumnPhysicalType.Nvarchar2,
            ColumnPhysicalType.Varchar2,
            ColumnPhysicalType.Blob,
            ColumnPhysicalType.Clob,
            ColumnPhysicalType.JSON
        ] : undefined,
        LogicalToPhysicalMapping: GlobalVariables.oracleAutoCreate ? {
            string: ColumnPhysicalType.Varchar2,
            byte: ColumnPhysicalType.Number,
            sbyte: ColumnPhysicalType.Number,
            decimal: ColumnPhysicalType.Number,
            int16: ColumnPhysicalType.Number,
            uint16: ColumnPhysicalType.Number,
            int32: ColumnPhysicalType.Number,
            uint32: ColumnPhysicalType.Number,
            int64: ColumnPhysicalType.Number,
            uint64: ColumnPhysicalType.Number,
            single: ColumnPhysicalType.BinaryFloat,
            double: ColumnPhysicalType.BinaryDouble,
            datetime: ColumnPhysicalType.Timestamp,
            datetimeoffset: ColumnPhysicalType.TimestampTimeZoneFull,
            timespan: ColumnPhysicalType.IntervalDaySecondFull,
            'byte[]': ColumnPhysicalType.Blob,
            boolean: ColumnPhysicalType.Number,
            guid: ColumnPhysicalType.Char,
            date: ColumnPhysicalType.Date
        } : undefined,
        PhysicalToLogicalMapping: GlobalVariables.oracleAutoCreate ? {
            date: ColumnType.DateTime,
            timestamp: ColumnType.DateTime,
            binary_double: ColumnType.Double,
            binary_float: ColumnType.Single,
            'timestamp with time zone': ColumnType.DateTimeOffset,
            'timestamp with local time zone': ColumnType.DateTimeOffset,
            'interval day to second': ColumnType.TimeSpan,
            'interval year to month': ColumnType.TimeSpan,
            number: {
                default: ColumnType.Double,
                GoogleBigQueryObject: ColumnType.Decimal
            },
            nchar: ColumnType.String,
            char: ColumnType.String,
            character: ColumnType.String,
            charactervarying: ColumnType.String,
            nvarchar2: ColumnType.String,
            varchar2: ColumnType.String,
            blob: ColumnType.ByteArray,
            clob: ColumnType.String,
            boolean: ColumnType.Boolean,
            json: ColumnType.String
        } : undefined,
        binaryPhysicalTypes,
        binaryLengthMap: {
            char: {
                minValue: 1,
                maxValue: 2000,
                supportMaxValue: false,
                unitList: ['BYTE', 'CHAR'],
                defaultUnit: 'BYTE',
                defaultValue: 1,
                extractRegex: '(\\s*(\\d+)\\s*([A-Z]+)\\s*)',
                regexFlag: 'i'
            },
            varchar2: {
                minValue: 1,
                maxValue: 32767,
                supportMaxValue: false,
                unitList: ['BYTE', 'CHAR'],
                defaultUnit: 'BYTE',
                required: true,
                extractRegex: '(\\s*(\\d+)\\s*([A-Z]+)\\s*)',
                regexFlag: 'i'
            },
            nchar: {
                minValue: 1,
                maxValue: 2000,
                supportMaxValue: false,
                defaultValue: 1
            },
            nvarchar2: {
                minValue: 1,
                maxValue: 32767,
                supportMaxValue: false,
                required: true
            }
        },
        customizedPrecisionKey: 'PhysicalPrecision',
        customizedScaleKey: 'PhysicalScale',
        precisionMap: {
            number: {
                minValue: 1,
                maxValue: 38,
                allowNull: true
            },
            'interval year to month': {
                minValue: 0,
                maxValue: 9,
                defaultValue: 2
            },
            'interval day to second': {
                minValue: 0,
                maxValue: 9,
                defaultValue: 2
            }
        },
        scaleMap: {
            number: {
                minValue: -84,
                maxValue: 127,
                allowNull: true
            },
            timestamp: {
                minValue: 0,
                maxValue: 9,
                defaultValue: 6
            },
            'timestamp with time zone': {
                minValue: 0,
                maxValue: 9,
                defaultValue: 6
            },
            'timestamp with local time zone': {
                minValue: 0,
                maxValue: 9,
                defaultValue: 6
            },
            'interval day to second': {
                minValue: 0,
                maxValue: 9,
                defaultValue: 6
            }
        },
        logicalToPhysicalDefaultRange: GlobalVariables.oracleAutoCreate ? {
            String: {
                length: 4000
            },
            Byte: {
                precision: 3,
                scale: 0
            },
            SByte: {
                precision: 3,
                scale: 0
            },
            Decimal: {
                precision: 38,
                scale: 9
            },
            Int16: {
                precision: 5,
                scale: 0
            },
            UInt16: {
                precision: 5,
                scale: 0
            },
            Int32: {
                precision: 10,
                scale: 0
            },
            UInt32: {
                precision: 10,
                scale: 0
            },
            Int64: {
                precision: 19,
                scale: 0
            },
            UInt64: {
                precision: 20,
                scale: 0
            },
            DateTime: {
                scale: 7
            },
            DateTimeOffset: {
                scale: 7
            },
            TimeSpan: {
                precision: 8,
                scale: 7
            },
            Guid: {
                length: 36
            },
        } : undefined,
    };

    return metadata;
};
